import { useParams, Link, Navigate } from 'react-router-dom';
import { CheckCircle2, MapPin, Phone, ArrowRight, MessageCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO, {
  localBusinessJsonLd,
  breadcrumbJsonLd,
  faqJsonLd,
} from '../components/SEO';
import { locationBySlug } from '../data/locations';
import { products, certifications, company } from '../mock';
import { CertBadge } from '../components/CertificationsStrip';

const WHATSAPP_NUMBER = '917014310785';

// Build differentiated, city-specific FAQs from the location data + a couple of
// evergreen ones. Keeps each page genuinely useful (and eligible for FAQ rich
// results) rather than a thin doorway page.
function cityFaqs(loc) {
  const primary = loc.industries[0];
  return [
    {
      q: `Do you supply welding electrodes in ${loc.city}?`,
      a: `Yes. Prakash Electrodes serves ${loc.city} and the wider ${loc.region} region. Orders are dispatched from our IS 814:2004-certified factory in Kota, Rajasthan, with bulk pricing and technical support available for workshops and industrial buyers in ${loc.city}.`,
    },
    {
      q: `Which electrode is best for ${primary} in ${loc.city}?`,
      a: `For most work in ${loc.city}\u2019s ${primary}, our WELDARC and CUTWELD E6013 electrodes are the go-to choice \u2014 stable arc, low spatter, and reliable strength. For heavier industrial repair, HITKARI GOLD offers deeper penetration. Share your base metal and joint type and our team will recommend the exact rod and current settings.`,
    },
    {
      q: `Can I get bulk pricing or samples delivered to ${loc.city}?`,
      a: `Absolutely. We regularly supply fabricators and plants across ${loc.state}. Contact our sales team for volume pricing, custom packaging, samples, and delivery scheduling to ${loc.city}.`,
    },
    {
      q: 'Are your electrodes certified?',
      a: 'Yes. Every brand is manufactured in compliance with IS 814:2004 (Bureau of Indian Standards), and our factory runs an ISO 9001:2015 quality management system.',
    },
  ];
}

export default function LocationPage() {
  const { citySlug } = useParams();
  const loc = locationBySlug(citySlug);

  // Unknown city → send to the directory (no thin 200 pages for random slugs).
  if (!loc) return <Navigate to="/welding-electrodes-in" replace />;

  const faqs = cityFaqs(loc);
  const industryList = loc.industries.join(', ');
  const path = `/welding-electrodes-in/${loc.slug}`;
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hi Prakash Electrodes, I'm in ${loc.city}, ${loc.state} and would like a price quote for welding electrodes.`
  )}`;
  const title = `Welding Electrodes in ${loc.city} — WELDARC, CUTWELD & More`;
  const description = `Buy premium welding electrodes in ${loc.city}, ${loc.state}. IS 814:2004-certified WELDARC, CUTWELD, HITKARI GOLD, SUNLIGHT-13 & BPL for ${industryList}. Bulk supply from Prakash Electrodes, Kota.`;

  return (
    <div>
      <SEO
        title={title}
        description={description}
        path={path}
        keywords={[
          `welding electrodes ${loc.city}`,
          `welding electrode supplier ${loc.city}`,
          `E6013 electrodes ${loc.city}`,
          `welding rods ${loc.state}`,
        ]}
        jsonLd={[
          localBusinessJsonLd({ city: loc.city, state: loc.state }),
          breadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Locations', path: '/welding-electrodes-in' },
            { name: loc.city, path },
          ]),
          faqJsonLd(faqs),
        ]}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#0b0b0d] text-white pt-40 pb-24 overflow-hidden noise">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute right-0 top-0 h-96 w-96 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-400 mb-4 flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5" /> {loc.city}, {loc.state}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-semibold leading-[1.05] max-w-4xl">
            Welding Electrodes in {loc.city}
          </h1>
          <p className="mt-6 text-white/70 text-lg max-w-2xl">
            {loc.blurb} Trusted E6013 rods, certified to IS 814:2004 and made in
            Kota since 1985.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-amber-500 text-black font-medium px-6 py-3 rounded-full hover:bg-amber-400 transition-colors"
            >
              Get pricing for {loc.city} <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={`tel:${company.phone}`}
              className="inline-flex items-center gap-2 border border-white/20 px-6 py-3 rounded-full hover:border-amber-400 transition-colors"
            >
              <Phone className="h-4 w-4" /> {company.phone}
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-whatsapp-hero-btn"
              className="inline-flex items-center gap-2 bg-[#25D366] text-black font-medium px-6 py-3 rounded-full hover:bg-[#1ebe5b] transition-colors"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp quote for {loc.city}
            </a>
          </div>
        </div>
      </section>

      {/* Local context */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <h2 className="font-display text-2xl md:text-3xl font-semibold mb-4">
          Serving {loc.city}&rsquo;s industry
        </h2>
        <p className="text-stone-600 max-w-3xl leading-relaxed">
          Welders and fabricators across {loc.city} and the {loc.region} region
          work in {industryList}. Each demands a rod that strikes easily, runs a
          quiet arc, and leaves minimal cleanup. Our five brands cover everything
          from thin sheet-metal and cosmetic work to heavy structural and
          maintenance welding &mdash; dispatched from our Kota factory with bulk
          pricing and hands-on technical support.
        </p>
      </section>

      {/* Product range */}
      <section className="bg-stone-50 py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl font-semibold mb-8">
            Our range available in {loc.city}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p) => (
              <div
                key={p.id}
                className="group bg-white rounded-2xl p-6 border border-stone-200"
                data-testid={`location-product-card-${p.id}`}
              >
                <div className="relative overflow-hidden aspect-[4/3] -mx-6 -mt-6 mb-5 rounded-t-2xl bg-gradient-to-br from-neutral-50 via-white to-neutral-100 border-b border-stone-200">
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      data-testid={`location-product-image-${p.id}`}
                      className={`${p.imgFit === 'cover' ? 'h-full w-full object-cover' : 'max-h-full max-w-full object-contain'} group-hover:scale-105 transition-transform duration-700`}
                    />
                  </div>
                </div>
                <div className="font-display text-lg font-semibold">{p.name}</div>
                <div className="font-mono text-[11px] uppercase tracking-wide text-amber-600 mt-1">
                  {p.subtitle}
                </div>
                <p className="text-sm text-stone-600 mt-3">{p.description}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.features.map((f) => (
                    <li
                      key={f}
                      className="flex items-center gap-2 text-sm text-stone-700"
                    >
                      <CheckCircle2 className="h-4 w-4 text-amber-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-amber-600 font-medium hover:gap-3 transition-all"
            >
              See full product specifications <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Trust / certifications */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {certifications.map((c) => (
            <div
              key={c.name}
              data-testid={`location-cert-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              className="rounded-xl border border-stone-200 p-5 text-center"
            >
              <div className="mx-auto mb-3 h-16 w-16 rounded-lg bg-stone-50 border border-stone-200 flex items-center justify-center p-2">
                <CertBadge cert={c} />
              </div>
              <div className="font-display font-semibold">{c.name}</div>
              <div className="text-xs text-stone-500 mt-1">{c.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-stone-50 py-16">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl font-semibold mb-8">
            Welding electrodes in {loc.city} &mdash; FAQs
          </h2>
          <div className="space-y-5">
            {faqs.map((f) => (
              <div
                key={f.q}
                className="bg-white rounded-xl border border-stone-200 p-6"
              >
                <h3 className="font-medium text-stone-900">{f.q}</h3>
                <p className="text-sm text-stone-600 mt-2 leading-relaxed">
                  {f.a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`WhatsApp us for a quote in ${loc.city}`}
        data-testid="location-whatsapp-float-btn"
        className="fixed bottom-5 right-5 z-50 inline-flex items-center gap-2 bg-[#25D366] text-black font-medium pl-4 pr-5 py-3 rounded-full shadow-lg shadow-black/20 hover:bg-[#1ebe5b] hover:-translate-y-0.5 transition-[transform,background-color]"
      >
        <MessageCircle className="h-5 w-5" />
        <span className="text-sm">Quote for {loc.city}</span>
      </a>
    </div>
  );
}
